import React, { useState, useEffect } from "react";
import {
    Users,
    Edit,
    Search,
    Trash2,
    X,
    Mail,
    Phone,
    Calendar,
    MapPin,
    Power,
    Plus
} from "react-feather";
import SessionControl from "../../components/SessionControl/sessioncontrol";
import CheckRestauSelected from "../../components/SessionControl/checkrestauselected";
import { all_routes } from "../../../Router/all_routes";
import { base_api_url } from "../../../environment.jsx";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "../../../feature-module/pages/menus/MyButton.css";
import "./restousers.css";

const RestoUsers = () => {
    // Initialisation des routes et de SweetAlert
    const route = all_routes;
    const MySwal = withReactContent(Swal);

    // États globaux pour la gestion des utilisateurs et du chargement
    const [loading, setLoading] = useState(false);
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [reload, setReload] = useState(false);

    // États pour la gestion des tables (serveurs)
    const [showTablesModal, setShowTablesModal] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [assignedTables, setAssignedTables] = useState([]);
    const [unassignedTables, setUnassignedTables] = useState([]);
    const [selectedUnassignedTables, setSelectedUnassignedTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);

    // États pour le Drawer (profil détaillé)
    const [showUserDrawer, setShowUserDrawer] = useState(false);
    const [drawerUser, setDrawerUser] = useState(null);

    // Stockage des assignations de tables pour l'affichage dans la liste
    const [allUsersTableAssignments, setAllUsersTableAssignments] = useState({});

    // États pour la recherche et les filtres
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [etiquettes, setEtiquettes] = useState([]);

    // Récupération sécurisée de l'ID du restaurant depuis le stockage local
    const getRestoId = () => {
        try {
            return JSON.parse(localStorage.getItem("user"))?.restoId;
        } catch (e) {
            return null;
        }
    };

    const ideresto = getRestoId();

    // Objet pour la création d'un nouvel utilisateur
    const [newUser, setNewUser] = useState({
        id_restaurant: ideresto,
        nom: "",
        prenom: "",
        email: "",
        mot_de_passe: "",
        role: "",
        etiquette: "",
    });

    // Objet pour l'édition d'un utilisateur existant
    const [editForm, setEditForm] = useState({
        id_restaurant: ideresto,
        nom: "",
        prenom: "",
        email: "",
        mot_de_passe: "",
        role: "",
        etiquette: "",
    });

    // Génération automatique du mot de passe à l'ouverture du modal d'ajout
    useEffect(() => {
        if (showAddModal) {
            const password = Math.floor(100000 + Math.random() * 900000).toString();
            setNewUser(prev => ({ ...prev, mot_de_passe: password }));
        }
    }, [showAddModal]);

    /**
     * Récupère les tables assignées à tous les serveurs
     * Effectué de manière séquentielle pour éviter de saturer les ressources réseau
     */
    const fetchAllServerTableAssignments = async (users) => {
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const servers = users.filter(u => u.role === 3 || u.role === "3");
            if (servers.length === 0) return;

            const assignments = { ...allUsersTableAssignments };

            for (const server of servers) {
                // On évite de re-télécharger si déjà en cache
                if (assignments[server.id_utilisateur]) continue;

                try {
                    const response = await axios.get(
                        `${base_api_url}api/tables_perimetre/${server.id_utilisateur}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (response.status === 200) {
                        assignments[server.id_utilisateur] = response.data.tables || [];
                        // Mise à jour progressive du state pour un affichage fluide
                        setAllUsersTableAssignments(prev => ({
                            ...prev,
                            [server.id_utilisateur]: response.data.tables || []
                        }));
                    }
                } catch (error) {
                    // Erreur silencieuse en arrière-plan
                }
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des périmètres:", error);
        }
    };

    /**
     * Récupère la liste des étiquettes (pôles de préparation)
     */
    const fetchEtiquettes = async () => {
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const rId = getRestoId();
            if (!rId) return;
            const response = await axios.get(`${base_api_url}api/etiquette/${rId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200 || response.status === 201) {
                const etiqList = response.data.etiquette || response.data.etiquettes || (Array.isArray(response.data) ? response.data : []);
                setEtiquettes(etiqList);
            }
        } catch (error) {
            console.error("Erreur etiquettes:", error);
        }
    };

    /**
     * Récupère la liste principale des utilisateurs du restaurant
     */
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const rId = getRestoId();

            if (!rId) {
                setLoading(false);
                return;
            }

            const response = await axios.get(`${base_api_url}api/users/${rId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200 || response.status === 201) {
                const users = response.data.utilisateur || [];
                setUtilisateurs(users);
                // On lance la récupération des périmètres de tables en tâche de fond
                fetchAllServerTableAssignments(users);
            }
        } catch (error) {
            console.error("Erreur fetch users:", error);
            if (error.response?.status === 404) {
                setUtilisateurs([]);
            } else {
                MySwal.fire({
                    icon: "error",
                    text: "Impossible de charger la liste des utilisateurs.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 5000,
                    customClass: { popup: "pixel-toast" },
                });
            }
        } finally {
            setLoading(false);
        }
    };

        // Fonction pour supprimer une serveuse
    const handleDeleteServeuse = async (user) => {
        const result = await MySwal.fire({
            title: 'Êtes-vous sûr ?',
            text: `Voulez-vous vraiment supprimer l'utilisateur "${user.nom}" ? Cette action est irréversible !`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ff6a3d',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
            customClass: { popup: "pixel-toast-delete" }
        });

        if (result.isConfirmed) {
            setLoading(true);
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            try {
                await axios.put(`${base_api_url}/user/users/byadmin/${user.id_utilisateur}`, 
                    { status: 0 }, 
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                MySwal.fire({
                    icon: "success",
                    text: "utilisateur supprimée avec succès.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    customClass: { popup: "pixel-toast" },
                });

                setReload(!reload);
            } catch (error) {
                console.error("Erreur lors de la suppression:", error);
                MySwal.fire({
                    icon: "error",
                    text: "Une erreur est survenue lors de la suppression.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    customClass: { popup: "pixel-toast" },
                });
            } finally {
                setLoading(false);
            }
        }
    };


    /**
     * Met à jour le pôle (étiquette) d'un préparateur
     */
    const updateUserEtiquette = async (user, newEtiquette) => {
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const rId = getRestoId();
            const payload = {
                ...user,
                id_restaurant: parseInt(rId),
                etiquette: newEtiquette
            };

            const response = await axios.put(
                `${base_api_url}user/users/byadmin/${user.id_utilisateur}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200 || response.status === 201) {
                setDrawerUser({ ...drawerUser, etiquette: newEtiquette });
                fetchUsers();
                MySwal.fire({
                    icon: "success",
                    text: `Pôle mis à jour : ${newEtiquette || "Aucun"}`,
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 2000,
                    customClass: { popup: "pixel-toast" }
                });
            }
        } catch (error) {
            console.error("Erreur etiquette update:", error);
            MySwal.fire({ icon: "error", text: "Impossible de modifier le pôle.", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000, customClass: { popup: "pixel-toast" } });
        }
    };

    /**
     * Enregistre un nouveau membre dans l'équipe
     */
    const handleAddRestoUser = async (e) => {
        e.preventDefault();
        if (!newUser.email.trim()) return;

        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const rId = getRestoId();
            const payload = {
                id_restaurant: parseInt(rId),
                nom: newUser.nom.trim(),
                prenom: newUser.prenom.trim(),
                role: parseInt(newUser.role),
                mot_de_passe: newUser.mot_de_passe.trim(),
                email: newUser.email.trim(),
            };

            // Ajouter l'étiquette seulement si le rôle est préparateur (4) et qu'une étiquette est sélectionnée
            if (parseInt(newUser.role) === 4 && newUser.etiquette) {
                payload.etiquette = newUser.etiquette;
            }

            const response = await axios.post(`${base_api_url}api/newUser`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200 || response.status === 201) {
                MySwal.fire({ icon: "success", title: "Utilisateur ajouté !", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000, customClass: { popup: "pixel-toast" } });
                setNewUser({ id_restaurant: rId, nom: "", prenom: "", mot_de_passe: "", role: "", email: "", etiquette: "" });
                setShowAddModal(false);
                fetchUsers();
            }
        } catch (error) {
            console.error("Error adding user:", error);
            MySwal.fire({ icon: "error", text: error.response?.data?.error || "Une erreur est survenue.", toast: true, position: "bottom-end", showConfirmButton: false, timer: 5000, customClass: { popup: "pixel-toast" } });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Met à jour les informations d'un utilisateur existant
     */
    const handleEditUtilisateur = async (e) => {
        e.preventDefault();
        if (!editForm.email.trim()) return;

        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const rId = getRestoId();

            // On conserve les anciennes données (CORS fix)
            const payload = {
                ...editingUser,
                id_restaurant: parseInt(rId),
                nom: editForm.nom.trim(),
                prenom: editForm.prenom.trim(),
                role: parseInt(editForm.role),
                mot_de_passe: editForm.mot_de_passe.trim(),
                email: editForm.email.trim(),
            };

            // Ajouter l'étiquette seulement si le rôle est préparateur (4) et qu'une étiquette est sélectionnée
            if (parseInt(editForm.role) === 4 && editForm.etiquette) {
                payload.etiquette = editForm.etiquette;
            } else if (parseInt(editForm.role) !== 4) {
                payload.etiquette = null; // Nettoyer si on change le rôle
            }

            const response = await axios.put(
                `${base_api_url}user/users/byadmin/${editingUser.id_utilisateur}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200 || response.status === 201) {
                MySwal.fire({ icon: "success", text: "Utilisateur modifié !", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000, customClass: { popup: "pixel-toast" } });
                setEditingUser(null);
                setShowEditModal(false);
                fetchUsers();
            }
        } catch (error) {
            console.error("Error editing user:", error);
            MySwal.fire({ icon: "error", text: error.response?.data?.message || "Une erreur est survenue.", toast: true, position: "bottom-end", showConfirmButton: false, timer: 5000, customClass: { popup: "pixel-toast" } });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Ouvre le formulaire d'édition avec les données pré-remplies
     */
    const openEditModal = (utilisateur) => {
        setEditingUser(utilisateur);
        setEditForm({
            nom: utilisateur.nom,
            prenom: utilisateur.prenom,
            mot_de_passe: utilisateur.mot_de_passe,
            role: utilisateur.role,
            email: utilisateur.email,
            etiquette: utilisateur.etiquette || "",
        });
        setShowEditModal(true);
    };

    /**
     * Récupère les tables actuellement assignées à un serveur
     */
    const fetchAssignedTables = async (selectedUserId) => {
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const response = await axios.get(`${base_api_url}api/tables_perimetre/${selectedUserId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.status === 200) {
                setAssignedTables(response.data.tables || []);
            }
        } catch (error) {
            console.error("Erreur tables assignées:", error);
            setAssignedTables([]);
        }
    };

    /**
     * Récupère les tables du restaurant qui ne sont pas encore assignées à cet utilisateur
     */
    const fetchUnassignedTables = async (selectedUserId) => {
        try {
            const rId = getRestoId();
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const response = await axios.get(`${base_api_url}api/tables_non_associes/${selectedUserId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.status === 200) {
                setUnassignedTables(response.data.tables_non_associes.filter(table => table.id_restaurant === rId) || []);
            }
        } catch (error) {
            setUnassignedTables([]);
        }
    };

    /**
     * Ouvre le modal de gestion des zones de service (tables)
     */
    const openTablesModal = async (utilisateur) => {
        setCurrentUserId(utilisateur.id_utilisateur);
        setLoadingTables(true);
        setShowTablesModal(true);
        setSelectedUnassignedTables([]);
        try {
            await Promise.all([
                fetchAssignedTables(utilisateur.id_utilisateur),
                fetchUnassignedTables(utilisateur.id_utilisateur)
            ]);
        } finally {
            setLoadingTables(false);
        }
    };

    /**
     * Envoie l'assignation des nouvelles tables sélectionnées
     */
    const assignTablesToUser = async () => {
        if (selectedUnassignedTables.length === 0) return;
        setLoadingTables(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const payload = { tables: selectedUnassignedTables };

            const response = await axios.post(
                `${base_api_url}api/tablelist_perimetre/${currentUserId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200 || response.status === 201) {
                MySwal.fire({ icon: "success", text: "Tables assignées !", toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000, customClass: { popup: "pixel-toast" } });
                setSelectedUnassignedTables([]);
                await Promise.all([
                    fetchAssignedTables(currentUserId),
                    fetchUnassignedTables(currentUserId)
                ]);
                fetchAllServerTableAssignments(utilisateurs);
            }
        } catch (error) {
            console.error("Erreur d'assignation:", error);
        } finally {
            setLoadingTables(false);
        }
    };

    /**
     * Gère la case à cocher pour la sélection multiple de tables
     */
    const handleUnassignedTableCheck = (tableId) => {
        setSelectedUnassignedTables((prev) =>
            prev.includes(tableId) ? prev.filter((id) => id !== tableId) : [...prev, tableId]
        );
    };

    /**
     * Affiche le profil détaillé dans le volet latéral (Drawer)
     */
    const openUserDrawer = (user) => {
        setDrawerUser(user);
        setShowUserDrawer(true);
        if (user.role === 3 || user.role === "3") {
            fetchAssignedTables(user.id_utilisateur);
            fetchUnassignedTables(user.id_utilisateur);
        }
    };

    // Initialisation au chargement de la page
    useEffect(() => {
        fetchUsers();
        fetchEtiquettes();
    }, []);

    // Logique de filtrage en temps réel pour la barre de recherche
    const filteredUsers = utilisateurs.filter(user => {
        const fullName = `${user.prenom} ${user.nom} ${user.email} ${user.etiquette || ""}`.toLowerCase();
        const matchesSearch = fullName.includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === "all" || user.role?.toString() === roleFilter;
        const matchesStatus = statusFilter === "all" || user.status?.toString() === statusFilter;
        return matchesSearch && matchesRole && matchesStatus;
    });

    // Génère les initiales pour l'avatar par défaut
    const getInitials = (user) => {
        return (user.prenom?.charAt(0) || "") + (user.nom?.charAt(0) || "");
    };

    const restaurantName = JSON.parse(localStorage.getItem("restaurant") || "{}")?.restaurant?.nom || "votre établissement";

    return (
        <div className="page-wrapper resto-users-container">
            {/* Contrôles de session obligatoires */}
            <SessionControl storageKey="user" propsup="email" redirectTo={route.signin} />
            <CheckRestauSelected storageKey="user" redirectTo={route.Restaurants} />

            {/* En-tête de la page */}
            <div className="resto-users-header">
                <div className="resto-users-title-area">
                    <h2>Équipes</h2>
                    <p>Gérez les accès collaborateurs de {restaurantName}.</p>
                </div>
                <button className="btn-add-member-modern" onClick={() => setShowAddModal(true)}>
                    <Plus size={16} className="d-md-none" />
                    <span className="d-none d-md-inline">Ajouter un membre</span>
                </button>
            </div>

            {/* Zone de recherche et filtres */}
            <div className="resto-users-filters" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div className="resto-users-search-wrapper" style={{ flex: '1', minWidth: '250px' }}>
                    <Search className="search-icon-modern" size={18} />
                    <input
                        type="text"
                        placeholder="Rechercher par nom, email ou pôle..."
                        className="search-input-modern"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <select 
                    className="form-control-modern" 
                    style={{ width: 'auto', minWidth: '160px' }}
                    value={roleFilter} 
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="all">Tous les rôles</option>
                    <option value="2">Administrateur</option>
                    <option value="3">Serveur</option>
                    <option value="4">Préparateur</option>
                </select>
                <select 
                    className="form-control-modern" 
                    style={{ width: 'auto', minWidth: '160px' }}
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">Tous les statuts</option>
                    <option value="1">Actif</option>
                    <option value="0">Inactif</option>
                </select>
            </div>

            {/* Liste des utilisateurs (Vue Tableau) */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="resto-users-empty">
                    <Users size={48} />
                    <h3>Aucun membre trouvé</h3>
                    <p>Commencez par ajouter votre premier collaborateur.</p>
                </div>
            ) : (
                <div className="resto-users-table-container">
                    <div className="users-table-header">
                        <div className="header-col">COLLABORATEUR</div>
                        <div className="header-col">RÔLE</div>
                        <div className="header-col">STATUT</div>
                        <div className="header-col">AFFECTATIONS</div>
                        <div className="header-col text-end">ACTIONS</div>
                    </div>

                    <div className="users-table-body">
                        {filteredUsers.map((user) => {
                            const isServer = user.role === 3 || user.role === "3";
                            const isPreparateur = user.role === 4 || user.role === "4";
                            const isGestionnaire = user.role === 2 || user.role === "2";
                            const userTables = isServer ? (allUsersTableAssignments[user.id_utilisateur] || []) : [];

                            return (
                                <div key={user.id_utilisateur || user.id} className="user-row" onClick={() => openUserDrawer(user)}>
                                    <div className="col-user">
                                        <div className="user-avatar-initials">{getInitials(user).toUpperCase()}</div>
                                        <div className="user-info-text">
                                            <span className="user-name">{user.prenom} {user.nom}</span>
                                            <span className="user-email">{user.email}</span>
                                        </div>
                                    </div>
                                    <div className="col-role">
                                        <span className="user-role-text">{isGestionnaire ? 'Administrateur' : isPreparateur ? 'Préparateur' : 'Serveur'}</span>
                                    </div>
                                    <div className="col-status">
                                        <span 
                                            className="badge-role-modern"
                                            style={{ 
                                                backgroundColor: user.status === 1 ? '#dcfce7' : '#fee2e2', 
                                                color: user.status === 1 ? '#166534' : '#991b1b',
                                                border: `1px solid ${user.status === 1 ? '#bbf7d0' : '#fecaca'}`
                                            }}
                                        >
                                            {user.status === 1 ? 'Actif' : 'Inactif'}
                                        </span>
                                    </div>
                                    <div className="col-affectations">
                                        {isPreparateur && user.etiquette ? (
                                            <span className="affectation-badge">{user.etiquette}</span>
                                        ) : isServer && userTables.length > 0 ? (
                                            userTables.map((t, idx) => <span key={idx} className="affectation-badge">Table {t.numero_table}</span>)
                                        ) : (
                                            <span className="none-text">Aucune</span>
                                        )}
                                    </div>
                                    <div className="col-actions">
                                        <button className="action-btn active" onClick={(e) => e.stopPropagation()} title="Actif"><Power size={18} /></button>
                                        <button className="action-btn" onClick={(e) => { e.stopPropagation(); openEditModal(user); }} title="Modifier"><Edit size={18} /></button>
                                        <button className="action-btn" onClick={(e) => {e.stopPropagation();  handleDeleteServeuse(user); }} title="Supprimer"><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Volet Latéral : Détails du profil */}
            {showUserDrawer && drawerUser && (
                <div className="side-drawer-overlay" onClick={() => setShowUserDrawer(false)}>
                    <div className="side-drawer-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="side-drawer-header">
                            <div className="user-card-avatar" style={{ width: '56px', height: '56px', fontSize: '20px' }}>{getInitials(drawerUser).toUpperCase()}</div>
                            <div className="drawer-user-info">
                                <h3>{drawerUser.prenom} {drawerUser.nom}</h3>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <div className={`badge-role-modern ${drawerUser.role === 2 || drawerUser.role === "2" ? 'badge-role-gestionnaire' : drawerUser.role === 4 || drawerUser.role === "4" ? 'badge-role-cuisinier' : 'badge-role-serveur'}`} style={{ marginBottom: 0 }}>
                                        {drawerUser.role === 2 || drawerUser.role === "2" ? 'Gestionnaire' : drawerUser.role === 4 || drawerUser.role === "4" ? 'Préparateur' : 'Serveur'}
                                    </div>
                                    {(drawerUser.role === 4 || drawerUser.role === "4") && drawerUser.etiquette && (
                                        <div className="badge-role-modern badge-etiquette-modern" style={{ marginBottom: 0 }}>{drawerUser.etiquette}</div>
                                    )}
                                </div>
                            </div>
                            <button className="drawer-close-btn" onClick={() => setShowUserDrawer(false)}><X size={20} /></button>
                        </div>
                        <div className="side-drawer-content">
                            {/* Statut du compte */}
                            <div className="account-status-card">
                                <div className="account-status-text"><h6>Compte actif</h6><p>Cet équipier peut se connecter</p></div>
                                <label className="switch-modern"><input type="checkbox" defaultChecked /><span className="slider-modern"></span></label>
                            </div>

                            {/* Section spécifique : Préparateur */}
                            {(drawerUser.role === 4 || drawerUser.role === "4") && (
                                <div className="account-status-card" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                                    <div className="account-status-text"><h6>Pôle de préparation</h6><p>Assignez ce préparateur à un pôle spécifique</p></div>
                                    <select className="form-control-modern" value={drawerUser.etiquette || ""} onChange={(e) => updateUserEtiquette(drawerUser, e.target.value)}>
                                        <option value="">Aucun pôle</option>
                                        {etiquettes.map((etiq) => <option key={etiq.id_etiquette || etiq.id} value={etiq.nom || etiq.code}>{etiq.nom || etiq.code || etiq.tag || "Pôle sans nom"}</option>)}
                                    </select>
                                </div>
                            )}

                            {/* Section spécifique : Serveur */}
                            {(drawerUser.role === 3 || drawerUser.role === "3") && (
                                <div className="account-status-card" onClick={() => { setShowUserDrawer(false); openTablesModal(drawerUser); }} style={{ cursor: 'pointer' }}>
                                    <div className="account-status-text"><h6>Assignation des tables</h6><p>Gérez le périmètre de service de ce serveur</p></div>
                                    <Edit size={18} color="#94a3b8" />
                                </div>
                            )}

                            {/* Informations de contact */}
                            <div className="drawer-section">
                                <span className="drawer-section-title">Informations générales</span>
                                <div className="info-item-modern"><Mail size={16} /> {drawerUser.email}</div>
                                <div className="info-item-modern"><Phone size={16} /> +237XXXXXXXXX</div>
                                <div className="info-item-modern"><Calendar size={16} /> Ajouté récemment</div>
                                <div className="info-item-modern"><MapPin size={16} /> {restaurantName}</div>
                            </div>
                            <button className="btn-delete-member"><Trash2 size={16} /> Supprimer le membre</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal : Ajouter un membre */}
            {showAddModal && (
                <div className="modal-modern">
                    <div className="modal-content-modern">
                        <button className="modal-close-modern" onClick={() => setShowAddModal(false)}><X size={20} /></button>
                        <div className="modal-header-text"><h3>Ajouter un membre</h3><p>Nouveau collaborateur pour votre équipe</p></div>
                        <form onSubmit={handleAddRestoUser}>
                            <div className="form-grid-modern">
                                <div className="form-group-modern"><label>Prénom *</label><input type="text" className="form-control-modern" placeholder="Jean" value={newUser.prenom} onChange={(e) => setNewUser({ ...newUser, prenom: e.target.value })} required /></div>
                                <div className="form-group-modern"><label>Nom *</label><input type="text" className="form-control-modern" placeholder="Dupont" value={newUser.nom} onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })} required /></div>
                            </div>
                            <div className="form-group-modern"><label>E-mail *</label><input type="email" className="form-control-modern" placeholder="jean.dupont@email.com" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required /></div>
                            <div className="form-group-modern"><label>Mot de passe (auto-généré)</label><input type="text" className="form-control-modern" value={newUser.mot_de_passe} readOnly /></div>
                            <div className="form-group-modern"><label>Rôle *</label>
                                <select className="form-control-modern" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} required>
                                    <option value="">Sélectionnez un rôle</option>
                                    <option value="2">Gestionnaire</option>
                                    <option value="3">Serveur</option>
                                    <option value="4">Préparateur</option>
                                </select>
                            </div>
                            
                            {(newUser.role === "4" || newUser.role === 4) && (
                                <div className="form-group-modern"><label>Pôle de préparation (Optionnel)</label>
                                    <select className="form-control-modern" value={newUser.etiquette} onChange={(e) => setNewUser({ ...newUser, etiquette: e.target.value })}>
                                        <option value="">Sélectionnez un pôle...</option>
                                        {etiquettes.map((etiq) => <option key={etiq.id_etiquette || etiq.id} value={etiq.nom || etiq.code}>{etiq.nom || etiq.code || etiq.tag || "Pôle sans nom"}</option>)}
                                    </select>
                                </div>
                            )}

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-cancel-modern" onClick={() => setShowAddModal(false)}>Annuler</button>
                                <button type="submit" className="btn-submit-modern" disabled={loading}>{loading ? 'Ajout...' : 'Ajouter'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal : Modifier un membre */}
            {showEditModal && editingUser && (
                <div className="modal-modern">
                    <div className="modal-content-modern">
                        <button className="modal-close-modern" onClick={() => setShowEditModal(false)}><X size={20} /></button>
                        <div className="modal-header-text"><h3>Modifier le membre</h3><p>Mise à jour pour {editingUser?.nom}</p></div>
                        <form onSubmit={handleEditUtilisateur}>
                            <div className="form-grid-modern">
                                <div className="form-group-modern"><label>Prénom *</label><input type="text" className="form-control-modern" value={editForm.prenom} onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })} required /></div>
                                <div className="form-group-modern"><label>Nom *</label><input type="text" className="form-control-modern" value={editForm.nom} onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })} required /></div>
                            </div>
                            <div className="form-group-modern"><label>E-mail *</label><input type="email" className="form-control-modern" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required /></div>
                            <div className="form-group-modern"><label>Rôle *</label>
                                <select className="form-control-modern" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} required>
                                    <option value="2">Gestionnaire</option>
                                    <option value="3">Serveur</option>
                                    <option value="4">Préparateur</option>
                                </select>
                            </div>

                            {(editForm.role === "4" || editForm.role === 4) && (
                                <div className="form-group-modern"><label>Pôle de préparation (Optionnel)</label>
                                    <select className="form-control-modern" value={editForm.etiquette} onChange={(e) => setEditForm({ ...editForm, etiquette: e.target.value })}>
                                        <option value="">Sélectionnez un pôle...</option>
                                        {etiquettes.map((etiq) => <option key={etiq.id_etiquette || etiq.id} value={etiq.nom || etiq.code}>{etiq.nom || etiq.code || etiq.tag || "Pôle sans nom"}</option>)}
                                    </select>
                                </div>
                            )}

                            <div className="modal-footer-modern">
                                <button type="button" className="btn-cancel-modern" onClick={() => setShowEditModal(false)}>Annuler</button>
                                <button type="submit" className="btn-submit-modern" disabled={loading}>{loading ? 'Enregistrement...' : 'Enregistrer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal : Gestion des tables (Périmètre Serveur) */}
            {showTablesModal && (
                <div className="modal-modern">
                    <div className="modal-content-modern" style={{ maxWidth: '700px' }}>
                        <button className="modal-close-modern" onClick={() => setShowTablesModal(false)}><X size={20} /></button>
                        <div className="modal-header-text"><h3>Gestion des tables</h3><p>Attribuez des zones de service à Jean.</p></div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                            <div>
                                <h6 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '12px', color: '#16a34a' }}>Tables assignées ({assignedTables.length})</h6>
                                <div style={{ maxHeight: '250px', overflowY: 'auto', background: '#f8fafc', borderRadius: '12px', padding: '12px' }}>
                                    {assignedTables.length === 0 ? <p style={{ fontSize: '13px', color: '#94a3b8' }}>Aucune table</p> :
                                        assignedTables.map(t => <div key={t.id_table} style={{ padding: '8px', background: '#fff', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}>{t.numero_table} <small style={{ color: '#94a3b8' }}>(Cap: {t.capacite})</small></div>)}
                                </div>
                            </div>
                            <div>
                                <h6 style={{ fontWeight: 600, fontSize: '14px', marginBottom: '12px', color: '#2563eb' }}>Tables disponibles ({unassignedTables.length})</h6>
                                <div style={{ maxHeight: '250px', overflowY: 'auto', background: '#f8fafc', borderRadius: '12px', padding: '12px' }}>
                                    {unassignedTables.length === 0 ? <p style={{ fontSize: '13px', color: '#94a3b8' }}>Aucune table</p> :
                                        unassignedTables.map(t => (
                                            <div key={t.id_table} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: '#fff', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                                                <input type="checkbox" checked={selectedUnassignedTables.includes(t.id_table)} onChange={() => handleUnassignedTableCheck(t.id_table)} />{t.numero_table}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-cancel-modern" onClick={() => setShowTablesModal(false)}>Fermer</button>
                            <button type="button" className="btn-submit-modern" onClick={assignTablesToUser} disabled={loadingTables || selectedUnassignedTables.length === 0}>
                                {loadingTables ? 'Assignation...' : `Affecter (${selectedUnassignedTables.length})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RestoUsers;
